// utils/tradeMarkers.js
// Trade marker utility — converts trade data to Lightweight Charts markers
// Used by Chart.jsx, TradesView.jsx, and future report components
//
// Marker design:
// Entry BUY  (long)  → arrowUp,   green,  belowBar, text='B'
// Entry SELL (short) → arrowUp,   red,    belowBar, text='S'
// Exit  SELL (close) → arrowDown, red,    aboveBar, text='X'
// Exit  BUY  (cover) → arrowDown, green,  aboveBar, text='X'
// Delivery entry BUY  → arrowUp,   green,  belowBar, text='D'
// Delivery exit  SELL → arrowDown, red,    aboveBar, text='D'

import { createSeriesMarkers } from 'lightweight-charts';

// ============================================================
// CONSTANTS
// ============================================================
const MARKER_COLORS = {
  green:  '#26a69a',
  red:    '#ef5350',
};
// Store primitive per series — keyed by series object
const seriesMarkerPrimitives = new WeakMap();

// ============================================================
// TIMESTAMP CONVERSION
// trade entry_date + entry_time are in IST
// Convert to unix seconds matching Chart.jsx toChartTime logic
// ============================================================
function toChartTime(dateStr, timeStr) {
  // +05:30 in string already handles IST conversion correctly
  const isoString = `${dateStr}T${timeStr || '09:15:00'}+05:30`;
  const d = new Date(isoString);
  return Math.floor(d.getTime() / 1000);
}

// ============================================================
// GET ENTRY MARKER CONFIG
// ============================================================
function getEntryMarker(trade) {
  const action     = (trade.entry_action || '').toLowerCase().trim();
  const isDelivery = (trade.instrument  || '').toLowerCase().trim() === 'delivery';
  const isBuy      = action === 'buy';

  return {
    time:     toChartTime(trade.entry_date, trade.entry_time),
    position: 'belowBar',
    shape:    'arrowUp',
    color:    isBuy ? MARKER_COLORS.green : MARKER_COLORS.red,
    text:     isDelivery ? 'D' : (isBuy ? 'B' : 'S'),
    id:       `entry_${trade.id}`,
    tradeId:  trade.id,
    side:     'entry',
  };
}

// ============================================================
// GET EXIT MARKER CONFIG
// ============================================================
function getExitMarker(trade) {
  if (!trade.exit_date) return null;

  const action     = (trade.exit_action || '').toLowerCase().trim();
  const isDelivery = (trade.instrument  || '').toLowerCase().trim() === 'delivery';
  const isBuy      = action === 'buy';

  return {
    time:     toChartTime(trade.exit_date, trade.exit_time),
    position: 'aboveBar',
    shape:    'arrowDown',
    color:    isBuy ? MARKER_COLORS.green : MARKER_COLORS.red,
    text:     isDelivery ? 'D' : 'X',
    id:       `exit_${trade.id}`,
    tradeId:  trade.id,
    side:     'exit',
  };
}

// ============================================================
// BUILD MARKERS FROM TRADES ARRAY
// ============================================================
export function buildMarkersFromTrades(trades) {
  if (!trades || trades.length === 0) return [];

  const markers = [];
  trades.forEach((trade) => {
    const entry = getEntryMarker(trade);
    if (entry) markers.push(entry);
    const exit = getExitMarker(trade);
    if (exit) markers.push(exit);
  });

  // Lightweight Charts requires markers sorted by time ASC
  markers.sort((a, b) => a.time - b.time);
  return markers;
}

// ============================================================
// APPLY MARKERS TO SERIES — static import, no dynamic import
// ============================================================
export function applyMarkersToSeries(series, markers) {
  if (!series) return;
  try {
    let primitive = seriesMarkerPrimitives.get(series);
    if (primitive) {
      primitive.setMarkers(markers || []);
    } else {
      primitive = createSeriesMarkers(series, markers || []);
      seriesMarkerPrimitives.set(series, primitive);
    }
  } catch (err) {
    console.error('Error applying markers:', err);
  }
}

// ============================================================
// REMOVE MARKERS FROM SERIES
// ============================================================
export function removeMarkersFromSeries(series) {
  if (!series) return;
  try {
    const primitive = seriesMarkerPrimitives.get(series);
    if (primitive) {
      primitive.setMarkers([]);
    }
  } catch (err) {
    console.error('Error removing markers:', err);
  }
}

// ============================================================
// GET MARKERS FOR SINGLE TRADE
// ============================================================
export function getMarkersForTrade(trade) {
  const markers = [];
  const entry   = getEntryMarker(trade);
  const exit    = getExitMarker(trade);
  if (entry) markers.push(entry);
  if (exit)  markers.push(exit);
  return markers;
}